import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  if (!currentCategory) {
    return (
      <>
        <SEO title="分类未找到" description="抱歉，您查找的分类不存在。" />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-[60vh] pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">分类未找到</h1>
            <p className="text-foreground-500 mb-8">抱歉，您查找的分类不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${currentCategory.name}`}
        description={currentCategory.description}
        keywords={`8x8x水蜜桃,${currentCategory.name},水蜜桃视频平台`}
        canonicalUrl={`https://8x8xpeach.com/category/${currentCategory.slug}`}
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - 8x8x水蜜桃`,
            description: currentCategory.description,
            url: `https://8x8xpeach.com/category/${currentCategory.slug}`,
          },
        ]}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory.name },
              ]}
            />
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${currentCategory.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">
                  共 {currentCategory.count} 篇内容
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-20">
          <div className="flex items-center gap-2 py-6 overflow-x-auto">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer border border-background-200/70"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      title={article.title}
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-4 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-file-list-3-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无内容</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}